/**
 * React Error Boundary component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Button,
} from "@/components/primitives";
import { AlertCircle, RefreshCw, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
	hasError: boolean;
	error: Error | null;
	errorInfo: ErrorInfo | null;
}

class ErrorBoundaryClass extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null,
		};
	}

	static getDerivedStateFromError(error: Error): State {
		// Update state so the next render will show the fallback UI
		return {
			hasError: true,
			error,
			errorInfo: null,
		};
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		// Log error to console (in production, this would go to error reporting service)
		console.error("ErrorBoundary caught an error:", error, errorInfo);

		this.setState({
			error,
			errorInfo,
		});

		// Call optional error handler
		if (this.props.onError) {
			this.props.onError(error, errorInfo);
		}

		// In production, you might want to log to an error reporting service here
		// Example: Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
	}

	handleReset = () => {
		this.setState({
			hasError: false,
			error: null,
			errorInfo: null,
		});
	};

	render() {
		if (this.state.hasError) {
			// Custom fallback UI
			if (this.props.fallback) {
				return this.props.fallback;
			}

			// Default fallback UI
			return (
				<ErrorFallback error={this.state.error} onReset={this.handleReset} />
			);
		}

		return this.props.children;
	}
}

interface ErrorFallbackProps {
	error: Error | null;
	onReset: () => void;
}

function ErrorFallback({ error, onReset }: ErrorFallbackProps) {
	const navigate = useNavigate();

	return (
		<div className="container mx-auto px-4 py-8 max-w-2xl">
			<Card>
				<CardHeader>
					<div className="flex items-center space-x-3">
						<AlertCircle className="h-8 w-8 text-red-500" />
						<CardTitle>Something went wrong</CardTitle>
					</div>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-muted-foreground">
						We're sorry, but something unexpected happened. Please try
						refreshing the page or returning to the home page.
					</p>

					{error && (
						<div className="bg-muted p-4 rounded-lg">
							<p className="text-sm font-mono text-muted-foreground">
								{error.message || "An unknown error occurred"}
							</p>
						</div>
					)}

					<div className="flex gap-3">
						<Button onClick={onReset} variant="default">
							<RefreshCw className="h-4 w-4 mr-2" />
							Try Again
						</Button>
						<Button onClick={() => navigate("/")} variant="outline">
							<Home className="h-4 w-4 mr-2" />
							Go Home
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

/**
 * Error Boundary component wrapper
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export function ErrorBoundary(props: Props) {
	return <ErrorBoundaryClass {...props} />;
}
